export type StaffLandingRole =
  | 'platform'
  | 'chain_admin'
  | 'manager'
  | 'supervisor'
  | 'budtender'
  | 'wholesale'
  | 'unknown'

type AccessUser = {
  role?: string
  pos_role?: string | null
  store_ids?: string[]
  access?: {
    dashboard?: { read?: boolean; edit?: boolean }
    analytics?: { read?: boolean }
    pos?: { read?: boolean }
    sale?: { read?: boolean }
    inventory?: { read?: boolean; edit?: boolean; create?: boolean }
    config?: { users?: { read?: boolean }; settings?: { read?: boolean } }
    wholesale?: { read?: boolean }
    sampleholdings?: { read?: boolean }
    sampleviewing?: { read?: boolean }
  }
}

/**
 * Same login → different app routes.
 * POS lives under /pos/*; wholesale under /dashboard, /apps, /inventory, etc.
 * Never send POS roles into the wholesale shell.
 */
export function detectStaffLandingRole(user: AccessUser): StaffLandingRole {
  const role = (user?.role || '').toLowerCase()
  if (role === 'superadmin') return 'platform'

  // Tenant owners use POS chain HQ (not wholesale menus)
  if (role === 'admin') return 'chain_admin'

  const posRole = String(user?.pos_role || '').toLowerCase()
  if (posRole === 'admin') return 'chain_admin'
  if (posRole === 'manager') return 'manager'
  if (posRole === 'supervisor') return 'supervisor'
  if (posRole === 'budtender') return 'budtender'

  const access = user?.access || {}
  const canPos =
    access.pos?.read === true || (Array.isArray(user.store_ids) && user.store_ids.length > 0)

  if (canPos) {
    const isManager =
      access.config?.users?.read === true ||
      access.analytics?.read === true ||
      access.inventory?.create === true ||
      (access.dashboard?.read === true && access.inventory?.edit === true) ||
      access.config?.settings?.read === true
    return isManager ? 'manager' : 'budtender'
  }

  // Wholesale-only workers (no POS permission / store assignment)
  if (
    access.wholesale?.read ||
    access.sale?.read ||
    access.dashboard?.read ||
    access.inventory?.read ||
    access.sampleholdings?.read ||
    access.sampleviewing?.read
  ) {
    return 'wholesale'
  }

  return 'unknown'
}

/** One login → route by role. Optional host override for pos.* */
export function resolveUnifiedPostLoginPath(user: AccessUser): {
  path: string
  landingRole: StaffLandingRole
  label: string
} {
  const host =
    typeof window !== 'undefined' ? window.location.host.toLowerCase() : ''
  const forcedPos =
    (process.env.NEXT_PUBLIC_APP_MODE || '').toLowerCase() === 'pos' || host.startsWith('pos.')

  const landingRole = detectStaffLandingRole(user)

  if (forcedPos) {
    if (landingRole === 'platform') {
      return { path: '/platform/tenants', landingRole, label: 'Platform' }
    }
    if (landingRole === 'wholesale' || landingRole === 'unknown') {
      return {
        path: '/auth/login',
        landingRole: 'unknown',
        label: 'No POS access',
      }
    }
    if (landingRole === 'chain_admin') {
      return { path: '/pos/chain', landingRole: 'manager', label: 'Chain Admin' }
    }
    return {
      path: '/pos/register',
      landingRole,
      label: 'POS',
    }
  }

  switch (landingRole) {
    case 'platform':
      return { path: '/platform/tenants', landingRole, label: 'Platform' }
    case 'chain_admin':
      return { path: '/pos/chain', landingRole, label: 'Chain Admin' }
    case 'manager':
      return { path: '/pos/register', landingRole, label: 'Store Manager' }
    case 'supervisor':
      return { path: '/pos/register', landingRole, label: 'Supervisor POS' }
    case 'budtender':
      return { path: '/pos/register', landingRole, label: 'Budtender POS' }
    case 'wholesale':
      return { path: '/dashboard/sales', landingRole, label: 'Wholesale' }
    default:
      return { path: '/auth/login', landingRole, label: 'Unknown' }
  }
}

/** @deprecated kept for any old imports */
export type LoginAppTarget = 'admin' | 'pos'
export const LOGIN_APP_STORAGE_KEY = 'mana_login_app_target'
export function getDefaultLoginAppTarget(): LoginAppTarget {
  return 'admin'
}
export function saveLoginAppTarget(_target: LoginAppTarget) {}
export function resolvePostLoginPath(_target: LoginAppTarget, user: AccessUser) {
  return resolveUnifiedPostLoginPath(user).path
}
